import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarDays,
  Check,
  CheckSquare,
  ClipboardList,
  Clock,
  Plus,
  Search,
  Target,
  Trash2,
  Users,
} from 'lucide-react';
import { FormDrawer } from '../forms';
import { ScheduleCalendar } from '../reusable';
import { userAgendaAPI } from '../../services/userAgendaAPI';

const TABS = [
  { key: 'calendar', label: 'Calendario', icon: CalendarDays },
  { key: 'activity', label: 'Actividades', icon: ClipboardList },
  { key: 'meeting', label: 'Reuniones', icon: Users },
  { key: 'sprint', label: 'Sprints', icon: Target },
  { key: 'todo', label: 'To-do', icon: CheckSquare },
];

const KINDS = [
  { value: 'activity', label: 'Actividad', color: '#2563eb' },
  { value: 'meeting', label: 'Reunión', color: '#7c3aed' },
  { value: 'sprint', label: 'Sprint', color: '#d97706' },
  { value: 'todo', label: 'To-do', color: '#0f766e' },
];

const STATUSES = ['Pendiente', 'En curso', 'Hecha', 'Cancelada'];
const PRIORITIES = ['Baja', 'Normal', 'Alta'];

const KIND_LABEL = Object.fromEntries(KINDS.map((item) => [item.value, item.label]));
const KIND_COLOR = Object.fromEntries(KINDS.map((item) => [item.value, item.color]));

const SERIF = 'Iowan Old Style, Palatino Linotype, Palatino, Georgia, serif';
const SANS = 'ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif';

const palette = (isDarkMode) =>
  isDarkMode
    ? {
        canvas: '#10161c',
        paper: '#171f28',
        ink: '#f6f0e6',
        muted: '#9aa6b2',
        line: 'rgba(246, 240, 230, 0.1)',
        accent: '#f2c28b',
        accentInk: '#1c1917',
        wash: 'rgba(242, 194, 139, 0.12)',
        danger: '#fca5a5',
        shadow: '0 22px 50px rgba(0, 0, 0, 0.32)',
      }
    : {
        canvas: '#f4efe6',
        paper: '#fffaf3',
        ink: '#1c1917',
        muted: '#78716c',
        line: 'rgba(28, 25, 23, 0.1)',
        accent: '#c45c26',
        accentInk: '#fffaf3',
        wash: 'rgba(196, 92, 38, 0.1)',
        danger: '#b91c1c',
        shadow: '0 22px 50px rgba(92, 64, 40, 0.1)',
      };

const todayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const emptyItem = (kind = 'activity', scheduledDate = '') => ({
  kind,
  title: '',
  notes: '',
  scheduled_date: scheduledDate || (kind === 'todo' ? '' : todayYmd()),
  start_time: '',
  end_time: '',
  status: 'Pendiente',
  priority: 'Normal',
  sprint_name: '',
});

const formatDateEs = (iso) => {
  if (!iso) return 'Sin fecha';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTimeRange = (start, end) => {
  const a = String(start || '').trim();
  const b = String(end || '').trim();
  if (a && b) return `${a}–${b}`;
  return a || b || '';
};

const weekdayLong = (date = new Date()) =>
  date.toLocaleDateString('es-CL', { weekday: 'long' }).replace(/^\w/, (c) => c.toUpperCase());

const monthYearLong = (date = new Date()) => {
  const raw = date.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
};

const statusTone = (statusRaw, isDarkMode) => {
  const status = String(statusRaw || '');
  if (status === 'Hecha') {
    return { bg: isDarkMode ? '#14532d' : '#dcfce7', color: isDarkMode ? '#bbf7d0' : '#166534' };
  }
  if (status === 'Cancelada') {
    return { bg: isDarkMode ? '#3f3f46' : '#f4f4f5', color: isDarkMode ? '#d4d4d8' : '#52525b' };
  }
  if (status === 'En curso') {
    return { bg: isDarkMode ? '#1e3a5f' : '#e0f2fe', color: isDarkMode ? '#7dd3fc' : '#0369a1' };
  }
  return { bg: isDarkMode ? '#422006' : '#fef3c7', color: isDarkMode ? '#fde68a' : '#92400e' };
};

const hexToRgba = (hex, alpha) => {
  const raw = String(hex || '').replace('#', '');
  if (raw.length !== 6) return `rgba(196, 92, 38, ${alpha})`;
  const n = parseInt(raw, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const Stamp = ({ icon, label, value, hint, color, ui }) => (
  <div
    style={{
      background: ui.paper,
      border: `1px solid ${ui.line}`,
      borderRadius: 22,
      padding: '16px 18px',
      minWidth: 0,
      boxShadow: ui.shadow,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: -18,
        right: -12,
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: hexToRgba(color, 0.14),
      }}
    />
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: ui.muted, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
      <span style={{ color }}>{icon}</span>
      {label}
    </div>
    <div style={{ marginTop: 10, fontSize: 32, fontWeight: 700, color: ui.ink, fontFamily: SERIF, lineHeight: 1 }}>{value}</div>
    {hint ? <div style={{ marginTop: 8, fontSize: 12, color: ui.muted }}>{hint}</div> : null}
  </div>
);

const UserAgendaWorkspace = ({ isDarkMode = false }) => {
  const ui = useMemo(() => palette(isDarkMode), [isDarkMode]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('calendar');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyItem());
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [calendarCollapsed, setCalendarCollapsed] = useState(false);
  const now = new Date();

  useEffect(() => {
    const requested = String(searchParams.get('tab') || '').trim().toLowerCase();
    if (TABS.some((item) => item.key === requested)) {
      setTab(requested);
    }
  }, [searchParams]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const rows = await userAgendaAPI.list();
      setItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setItems([]);
      setError(err?.message || 'No se pudo cargar la agenda.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const inputStyle = {
    height: 38,
    borderRadius: 999,
    border: `1px solid ${ui.line}`,
    background: ui.paper,
    color: ui.ink,
    padding: '0 14px',
    fontSize: 13,
    boxSizing: 'border-box',
    fontFamily: SANS,
  };
  const ghostBtn = {
    height: 30,
    width: 30,
    borderRadius: 999,
    border: `1px solid ${ui.line}`,
    background: 'transparent',
    color: ui.muted,
    padding: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const today = todayYmd();
  const openItems = useMemo(
    () => items.filter((row) => !row.is_done && row.status !== 'Cancelada'),
    [items]
  );
  const todayItems = useMemo(
    () => openItems.filter((row) => row.scheduled_date === today),
    [openItems, today]
  );
  const upcomingMeetings = useMemo(
    () =>
      openItems.filter(
        (row) => row.kind === 'meeting' && (!row.scheduled_date || row.scheduled_date >= today)
      ),
    [openItems, today]
  );
  const openTodos = useMemo(() => openItems.filter((row) => row.kind === 'todo'), [openItems]);
  const upcomingRail = useMemo(
    () =>
      openItems
        .filter((row) => row.scheduled_date && row.scheduled_date >= today)
        .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)) || String(a.start_time || '').localeCompare(String(b.start_time || '')))
        .slice(0, 8),
    [openItems, today]
  );

  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((row) => {
      if (tab !== 'calendar' && row.kind !== tab) return false;
      if (statusFilter && row.status !== statusFilter) return false;
      if (!q) return true;
      return [row.title, row.notes, row.sprint_name].join(' ').toLowerCase().includes(q);
    });
  }, [items, tab, statusFilter, query]);

  const calendarEvents = useMemo(
    () =>
      items
        .filter((row) => row.scheduled_date && row.status !== 'Cancelada')
        .map((row) => ({
          id: `agenda-${row.id}`,
          date: row.scheduled_date,
          title: row.title,
          subtitle: [KIND_LABEL[row.kind], formatTimeRange(row.start_time, row.end_time), row.sprint_name]
            .filter(Boolean)
            .join(' · '),
          status: row.status,
          color: KIND_COLOR[row.kind] || '#2563eb',
          row,
        })),
    [items]
  );

  const sprintGroups = useMemo(() => {
    const map = new Map();
    visibleItems.forEach((row) => {
      const name = String(row.sprint_name || '').trim() || 'Sin sprint';
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(row);
    });
    return Array.from(map.entries());
  }, [visibleItems]);

  const changeTab = (nextTab) => {
    setTab(nextTab);
    setSearchParams(nextTab === 'calendar' ? {} : { tab: nextTab }, { replace: true });
  };

  const openCreate = (kind = 'activity', scheduledDate = '') => {
    const resolvedKind = TABS.some((item) => item.key === kind && item.key !== 'calendar')
      ? kind
      : tab !== 'calendar'
        ? tab
        : 'activity';
    setEditingId(null);
    setFormError('');
    setForm(emptyItem(resolvedKind, scheduledDate));
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setFormError('');
    setForm({
      kind: row.kind || 'activity',
      title: row.title || '',
      notes: row.notes || '',
      scheduled_date: row.scheduled_date || '',
      start_time: row.start_time || '',
      end_time: row.end_time || '',
      status: row.status || 'Pendiente',
      priority: row.priority || 'Normal',
      sprint_name: row.sprint_name || '',
    });
    setFormOpen(true);
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveForm = async () => {
    try {
      setIsSaving(true);
      setFormError('');
      const payload = {
        ...form,
        scheduled_date: form.scheduled_date || null,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
      };
      if (editingId) {
        await userAgendaAPI.update(editingId, payload);
      } else {
        await userAgendaAPI.create(payload);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar el ítem.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDone = async (row) => {
    try {
      await userAgendaAPI.markDone(row.id, !row.is_done);
      await load();
    } catch (err) {
      window.alert(err?.message || 'No se pudo actualizar el ítem.');
    }
  };

  const removeItem = async (row) => {
    if (!window.confirm(`¿Eliminar “${row.title}”?`)) return;
    try {
      await userAgendaAPI.remove(row.id);
      await load();
    } catch (err) {
      window.alert(err?.message || 'No se pudo eliminar el ítem.');
    }
  };

  const formSections = useMemo(
    () => [
      {
        key: 'main',
        title: 'Detalle',
        defaultOpen: true,
        fields: [
          {
            key: 'kind',
            label: 'Tipo',
            type: 'select',
            options: KINDS.map((item) => ({ value: item.value, label: item.label })),
          },
          { key: 'title', label: 'Título', type: 'text', fullWidth: true },
          { key: 'notes', label: 'Notas', type: 'textarea', rows: 3, fullWidth: true },
          { key: 'scheduled_date', label: 'Fecha', type: 'date' },
          { key: 'start_time', label: 'Inicio', type: 'time' },
          { key: 'end_time', label: 'Término', type: 'time' },
          { key: 'status', label: 'Estado', type: 'select', options: STATUSES.map((value) => ({ value, label: value })) },
          { key: 'priority', label: 'Prioridad', type: 'select', options: PRIORITIES.map((value) => ({ value, label: value })) },
          ...(form.kind === 'sprint'
            ? [{ key: 'sprint_name', label: 'Sprint', type: 'text', placeholder: 'Ej. Sprint 14', fullWidth: true }]
            : []),
        ],
      },
    ],
    [form.kind]
  );

  const renderItemRow = (row) => {
    const tone = statusTone(row.status, isDarkMode);
    const overdue = row.scheduled_date && row.scheduled_date < today && !row.is_done && row.status !== 'Cancelada';
    const kindColor = KIND_COLOR[row.kind] || ui.accent;
    const timeLabel = formatTimeRange(row.start_time, row.end_time);
    return (
      <div
        key={row.id}
        style={{
          display: 'grid',
          gridTemplateColumns: '72px minmax(0, 1fr) auto',
          gap: 12,
          alignItems: 'center',
          padding: '14px 16px',
          borderBottom: `1px solid ${ui.line}`,
          background: overdue ? hexToRgba('#b91c1c', isDarkMode ? 0.16 : 0.06) : 'transparent',
        }}
      >
        <div style={{ textAlign: 'right', paddingRight: 4 }}>
          <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 700, color: overdue ? ui.danger : ui.ink }}>
            {timeLabel ? String(row.start_time || '').slice(0, 5) || '—' : row.scheduled_date ? formatDateEs(row.scheduled_date).split(' ')[0] : '—'}
          </div>
          <div style={{ fontSize: 10, color: ui.muted, marginTop: 2, letterSpacing: '0.04em' }}>
            {timeLabel || (overdue ? 'Vencido' : row.scheduled_date ? 'Día' : 'Sin fecha')}
          </div>
        </div>
        <button
          type="button"
          onClick={() => openEdit(row)}
          style={{
            minWidth: 0,
            textAlign: 'left',
            border: 'none',
            background: 'transparent',
            color: ui.ink,
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <span style={{ width: 4, alignSelf: 'stretch', minHeight: 28, borderRadius: 999, background: kindColor, flexShrink: 0 }} />
          <span style={{ minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 700,
                textDecoration: row.is_done ? 'line-through' : 'none',
                opacity: row.is_done ? 0.62 : 1,
              }}
            >
              {row.title}
            </span>
            <span style={{ display: 'block', fontSize: 12, color: ui.muted, marginTop: 3 }}>
              {[KIND_LABEL[row.kind], formatDateEs(row.scheduled_date), timeLabel, row.sprint_name].filter(Boolean).join(' · ')}
            </span>
            {row.notes ? (
              <span style={{ display: 'block', fontSize: 12, color: ui.muted, marginTop: 6, whiteSpace: 'pre-wrap' }}>{row.notes}</span>
            ) : null}
          </span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '4px 8px',
              borderRadius: 999,
              background: tone.bg,
              color: tone.color,
              whiteSpace: 'nowrap',
            }}
          >
            {row.status}
          </span>
          <button
            type="button"
            onClick={() => toggleDone(row)}
            title={row.is_done ? 'Marcar pendiente' : 'Marcar hecha'}
            style={{
              ...ghostBtn,
              background: row.is_done ? kindColor : 'transparent',
              color: row.is_done ? '#fff' : ui.muted,
              borderColor: row.is_done ? kindColor : ui.line,
            }}
          >
            <Check size={13} />
          </button>
          <button type="button" onClick={() => removeItem(row)} style={ghostBtn} title="Eliminar">
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    );
  };

  const listEmpty = !isLoading && visibleItems.length === 0;
  const heroHint =
    todayItems.length === 0
      ? 'Hoy está libre. Agenda algo cuando quieras.'
      : todayItems.length === 1
        ? 'Hoy tienes 1 compromiso abierto.'
        : `Hoy tienes ${todayItems.length} compromisos abiertos.`;

  return (
    <div
      style={{
        fontFamily: SANS,
        color: ui.ink,
        background: isDarkMode
          ? 'radial-gradient(900px 420px at 8% -10%, #1e3a4c 0%, transparent 55%), radial-gradient(700px 280px at 100% 0%, #2a2418 0%, transparent 46%), #10161c'
          : 'radial-gradient(980px 420px at 0% -18%, #fde8d0 0%, transparent 48%), radial-gradient(760px 320px at 100% 0%, #dceee7 0%, transparent 42%), #f4efe6',
        borderRadius: 28,
        padding: 18,
        minHeight: 'calc(100vh - 140px)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 16,
          flexWrap: 'wrap',
          padding: '8px 8px 18px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18 }}>
          <div
            style={{
              fontFamily: SERIF,
              fontSize: 88,
              lineHeight: 0.8,
              letterSpacing: '-0.04em',
              color: ui.ink,
            }}
          >
            {now.getDate()}
          </div>
          <div style={{ paddingBottom: 6 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: ui.accent, fontWeight: 800 }}>
              Mi agenda
            </div>
            <div style={{ fontFamily: SERIF, fontSize: 28, marginTop: 4, lineHeight: 1.1 }}>{weekdayLong(now)}</div>
            <div style={{ fontSize: 13, color: ui.muted, marginTop: 4 }}>{monthYearLong(now)}</div>
            <div style={{ fontSize: 13, color: ui.ink, marginTop: 10 }}>{heroHint}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => openCreate()}
          style={{
            height: 44,
            borderRadius: 999,
            border: 'none',
            background: ui.accent,
            color: ui.accentInk,
            padding: '0 18px',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: ui.shadow,
          }}
        >
          <Plus size={16} />
          Nuevo
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Stamp icon={<CalendarDays size={14} />} label="Hoy" value={todayItems.length} hint="Abiertos de hoy" color="#c45c26" ui={ui} />
        <Stamp icon={<ClipboardList size={14} />} label="Pendientes" value={openItems.length} hint="Sin terminar" color="#2563eb" ui={ui} />
        <Stamp icon={<Users size={14} />} label="Reuniones" value={upcomingMeetings.length} hint="Próximas" color="#7c3aed" ui={ui} />
        <Stamp icon={<CheckSquare size={14} />} label="To-do" value={openTodos.length} hint="Tareas abiertas" color="#0f766e" ui={ui} />
      </div>

      <div
        role="tablist"
        aria-label="Secciones de mi agenda"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
        }}
      >
        {TABS.map((item) => {
          const Icon = item.icon;
          const active = tab === item.key;
          const count =
            item.key === 'calendar'
              ? items.filter((row) => row.scheduled_date).length
              : items.filter((row) => row.kind === item.key).length;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => changeTab(item.key)}
              style={{
                height: 40,
                borderRadius: 999,
                border: `1px solid ${active ? ui.ink : ui.line}`,
                background: active ? ui.ink : ui.paper,
                color: active ? ui.paper : ui.ink,
                padding: '0 14px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Icon size={14} />
              {item.label}
              <span
                style={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: 999,
                  padding: '0 6px',
                  fontSize: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: active ? hexToRgba('#fffaf3', 0.16) : ui.wash,
                  color: active ? ui.paper : ui.ink,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {error ? (
        <div style={{ fontSize: 13, color: ui.danger, marginBottom: 12 }}>{error}</div>
      ) : null}

      {tab !== 'calendar' ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <Search size={14} color={ui.muted} style={{ position: 'absolute', left: 14, top: 12 }} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar título, notas o sprint…"
              style={{ ...inputStyle, width: '100%', paddingLeft: 36 }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            style={{ ...inputStyle, width: 180 }}
          >
            <option value="">Todos los estados</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {tab === 'calendar' ? (
        <div className="agenda-cal-layout">
          <style>{`
            .agenda-cal-layout {
              display: grid;
              grid-template-columns: minmax(0, 1fr);
              gap: 14px;
              align-items: start;
            }
            @media (min-width: 1180px) {
              .agenda-cal-layout {
                grid-template-columns: minmax(0, 1fr) minmax(240px, 300px);
              }
            }
          `}</style>
          <div style={{ minWidth: 0, overflowX: 'auto' }}>
          <ScheduleCalendar
            events={calendarEvents}
            isDarkMode={isDarkMode}
            appearance="planner"
            title="Esta semana"
            defaultView="week"
            emptyLabel="Libre"
            collapsed={calendarCollapsed}
            onToggleCollapsed={() => setCalendarCollapsed((prev) => !prev)}
            onEventClick={(evt) => evt.row && openEdit(evt.row)}
            onDayClick={(ymd) => openCreate('activity', ymd)}
          />
          </div>
          <aside
            style={{
              background: ui.paper,
              border: `1px solid ${ui.line}`,
              borderRadius: 22,
              padding: 16,
              boxShadow: ui.shadow,
              minHeight: 280,
            }}
          >
            <div style={{ fontFamily: SERIF, fontSize: 20, marginBottom: 4 }}>Próximo</div>
            <div style={{ fontSize: 12, color: ui.muted, marginBottom: 14 }}>Hoy y lo que sigue</div>
            {upcomingRail.length === 0 ? (
              <div style={{ fontSize: 13, color: ui.muted }}>Nada agendado todavía.</div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {upcomingRail.map((row) => {
                  const kindColor = KIND_COLOR[row.kind] || ui.accent;
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => openEdit(row)}
                      style={{
                        textAlign: 'left',
                        border: `1px solid ${ui.line}`,
                        background: hexToRgba(kindColor, 0.08),
                        borderRadius: 14,
                        padding: '10px 12px',
                        cursor: 'pointer',
                        color: ui.ink,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, color: ui.muted }}>
                        <span style={{ color: kindColor, fontWeight: 700 }}>{KIND_LABEL[row.kind]}</span>
                        <span>{row.scheduled_date === today ? 'Hoy' : formatDateEs(row.scheduled_date)}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>{row.title}</div>
                      {formatTimeRange(row.start_time, row.end_time) ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 11, color: ui.muted }}>
                          <Clock size={11} />
                          {formatTimeRange(row.start_time, row.end_time)}
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </aside>
        </div>
      ) : null}

      {tab === 'sprint' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {isLoading ? <div style={{ fontSize: 13, color: ui.muted }}>Cargando sprints…</div> : null}
          {listEmpty ? (
            <div style={{ fontSize: 13, color: ui.muted, padding: 16, background: ui.paper, borderRadius: 22, border: `1px dashed ${ui.line}` }}>
              Aún no hay seguimiento de sprint. Crea un ítem y asígnale un nombre de sprint.
            </div>
          ) : null}
          {sprintGroups.map(([name, rows]) => {
            const done = rows.filter((row) => row.is_done).length;
            const pct = rows.length ? Math.round((done / rows.length) * 100) : 0;
            return (
              <div
                key={name}
                style={{
                  background: ui.paper,
                  border: `1px solid ${ui.line}`,
                  borderRadius: 22,
                  overflow: 'hidden',
                  boxShadow: ui.shadow,
                }}
              >
                <div style={{ padding: '14px 16px 10px', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <strong style={{ fontFamily: SERIF, fontSize: 18 }}>{name}</strong>
                  <span style={{ fontSize: 12, color: ui.muted }}>
                    {done}/{rows.length} hechas
                  </span>
                </div>
                <div style={{ height: 4, margin: '0 16px 8px', borderRadius: 999, background: ui.line, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: ui.accent }} />
                </div>
                {rows.map(renderItemRow)}
              </div>
            );
          })}
        </div>
      ) : null}

      {tab !== 'calendar' && tab !== 'sprint' ? (
        <div
          style={{
            background: ui.paper,
            border: `1px solid ${ui.line}`,
            borderRadius: 22,
            overflow: 'hidden',
            boxShadow: ui.shadow,
          }}
        >
          {isLoading ? <div style={{ fontSize: 13, color: ui.muted, padding: 16 }}>Cargando…</div> : null}
          {listEmpty ? (
            <div style={{ fontSize: 13, color: ui.muted, padding: 28, textAlign: 'center' }}>
              No hay {KIND_LABEL[tab]?.toLowerCase() || 'ítems'} todavía.
            </div>
          ) : null}
          {visibleItems.map(renderItemRow)}
        </div>
      ) : null}

      <FormDrawer
        isOpen={formOpen}
        onClose={() => !isSaving && setFormOpen(false)}
        onSave={saveForm}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        title={editingId ? 'Editar ítem' : 'Nuevo ítem'}
        subtitle={KIND_LABEL[form.kind] || 'Agenda'}
        sections={formSections}
        formData={form}
        onChange={handleFormChange}
        formError={formError}
        saveLabel={editingId ? 'Guardar' : 'Crear'}
        width={520}
      />
    </div>
  );
};

export default UserAgendaWorkspace;
